import React, { useContext } from 'react';
import { Redirect } from 'react-router-dom';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { withFormik } from 'formik';

import { TextButtonRegularSmall as BackButton } from '@xingternal/malt-button';
import { testable } from '@xingternal/malt-util';
import { QA_MALT } from '@xingternal/malt-constants';

import DigitInput from '../../../Components/DigitInput/DigitInput';
import IllustrationPlaceholder from './../../../Components/Illustrations/IllustrationPlaceholder';
import ResendCountdown from '../../../Components/ResendCountdown/ResendCountdown';
import PageviewTracker from '../../../Components/PageviewTracker/PageviewTracker';
import withActionTracker from '../../../Components/PageviewTracker/withActionTracker';
import { CustomH, CustomP } from './../../../Components/CustomMalt/Typography';

import Store from '../../../store';
import api from './../../../utils/api';
import messages from './messages';
import styles from './PasswordRecoveryConfirm.css';

const TestableForm = testable(QA_MALT, 'form');

const withForm = withFormik({
  mapPropsToValues: () => ({
    token: '',
  }),
  validateOnBlur: false,
  validateOnChange: false,
  handleSubmit: (
    { token },
    {
      setErrors,
      setSubmitting,
      props: {
        history: { replace, push },
        track,
      },
    }
  ) => {
    api
      .post('/recovery/password/resets/code', { token })
      .then(({ success }) => {
        if (success) replace('/recovery/password/new');
      })
      .catch(err => {
        track('welcome_login_password_recovery_error_code');
        if (err.jsonResponse && err.jsonResponse.error === 'LOGIN_REQUIRED') {
          push({
            pathname: '/recovery',
            search: `?error=${err.jsonResponse.error}`,
          });
          return;
        }

        setErrors({ token: err.jsonResponse.error });
        setSubmitting(false);
      });
  },
});

const PasswordRecoveryConfirm = ({
  handleSubmit,
  setFieldValue,
  setErrors,
  errors,
  isSubmitting,
  submitForm,
}) => {
  const {
    state: { username, knownUser },
  } = useContext(Store);

  if (!username) {
    return <Redirect to={'/recovery'} />;
  }

  const onResendSuccess = () => setErrors(false);
  const onResendError = err => {
    const errorString = err && err.jsonResponse && err.jsonResponse.error;
    errorString && setErrors({ code: errorString });
  };

  return (
    <>
      <PageviewTracker
        pageName={'wbm/Welcome/login/recovery/password/confirm'}
      />

      <IllustrationPlaceholder />

      <TestableForm
        onSubmit={handleSubmit}
        method={'POST'}
        testId={'code-input-form'}
      >
        <CustomH className={styles.textAlignCenter}>
          <FormattedMessage {...messages.title} />
        </CustomH>

        <CustomP className={styles.textAlignCenter}>
          <FormattedMessage
            {...messages.description}
            values={{ username: <b>{username}</b> }}
          />
        </CustomP>

        <DigitInput
          onChange={value => {
            setFieldValue('token', value);
          }}
          message={errors && errors.token}
          autoFocus
          onComplete={submitForm}
          disabled={isSubmitting}
        />

        <ResendCountdown
          resendEndpoint={'/recovery/password/reset_requests'}
          resendPayload={knownUser ? null : { username }}
          onSuccess={onResendSuccess}
          onError={onResendError}
          center
        />

        {!knownUser && (
          <BackButton
            to={'/recovery'}
            testId={'correct-email-link'}
            className={styles.fullWidth}
          >
            <FormattedMessage {...messages.backLinkText} />
          </BackButton>
        )}
      </TestableForm>
    </>
  );
};

PasswordRecoveryConfirm.propTypes = {
  errors: PropTypes.object,
  handleSubmit: PropTypes.func,
  isSubmitting: PropTypes.bool,
  setErrors: PropTypes.func,
  setFieldValue: PropTypes.func.isRequired,
  submitForm: PropTypes.func.isRequired,
  track: PropTypes.func,
};

export default withActionTracker(withForm(PasswordRecoveryConfirm));
